import React, { Fragment, useMemo } from 'react';

import { useTheme } from '@cobalt/react-theme-provider';
import Box from '@cobalt/react-box';
import Flex from '@cobalt/react-flex';
import Icon from '@cobalt/react-icon';
import { ClickableItem, Item } from '@cobalt/react-list';
import { Text } from '@cobalt/react-typography';
import Divider from '@cobalt/react-divider';

import { SelectOptGroupType, SelectOptionType } from '../../../index';

import { ListorProps } from './index';
import { groupOptions, isOptGroup } from '../util';

interface SingleListorProps extends ListorProps {
  selectedValue: string;
}

interface SingleListorContentProps {
  item: SelectOptionType;
  selectedValue: string;
}

function GenericContent(props: SingleListorContentProps) {
  const { item, selectedValue } = props;

  const iconVisible = useMemo(() => {
    return !!item.iconName || item.value === selectedValue;
  }, [item.iconName, item.value, selectedValue]);

  return (
    <Flex padding="2">
      {/* Icon */}
      <Icon name={item.iconName || 'check'} size="tiny" style={{ visibility: iconVisible ? 'visible' : 'hidden' }} />

      {/* Option label */}
      <Box style={{ overflowX: 'hidden' }} paddingLeft="2" grow>
        {!item.renderOption ? (
          <Text truncated title={item.label}>
            {item.label}
          </Text>
        ) : (
          item.renderOption()
        )}
      </Box>
    </Flex>
  );
}

function ContentWithDescription(props: SingleListorContentProps) {
  const { item, selectedValue } = props;

  const theme = useTheme();

  const iconVisible = useMemo(() => {
    return !!item.iconName || item.value === selectedValue;
  }, [item.iconName, item.value, selectedValue]);

  return (
    <Flex padding="2">
      {/* Icon */}
      <Icon name={item.iconName || 'check'} size="tiny" style={{ visibility: iconVisible ? 'visible' : 'hidden' }} />

      <Box style={{ overflowX: 'hidden' }} paddingLeft="2" grow>
        {/* Option label */}
        <Text truncated title={item.label}>
          {item.label}
        </Text>

        {/* Description */}
        {item.description && (
          <Text color={theme.gray600} size="small" truncated title={item.description}>
            {item.description}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

function SingleListor(props: SingleListorProps) {
  const { selectedValue, options, onChangeSelect } = props;

  const theme = useTheme();

  const groupedOptions = useMemo(() => {
    return groupOptions(options);
  }, [options]);

  const optionNode = (item: SelectOptionType, isLast?: boolean) => (
    <>
      <ClickableItem
        data-testid="listorOption"
        key={item.value}
        style={{ marginLeft: theme.baseSpacing2, marginRight: theme.baseSpacing2 }}
        selected={item.value === selectedValue}
        disabled={item.disabled}
        onClick={() => onChangeSelect(item.value)}
      >
        {!item.description ? (
          <GenericContent item={item} selectedValue={selectedValue} />
        ) : (
          <ContentWithDescription item={item} selectedValue={selectedValue} />
        )}
      </ClickableItem>

      {item.borderBottom && !isLast && (
        <Box paddingBottom="1">
          <Divider />
        </Box>
      )}
    </>
  );

  const optGroupNode = (groupItem: SelectOptGroupType) => (
    <Fragment key={groupItem.key}>
      {groupItem.label && (
        <Item>
          <Box padding="2" paddingBottom="1">
            <Text color="var(--gray-600)" weight="medium">
              {groupItem.label}
            </Text>
          </Box>
        </Item>
      )}

      {groupItem.options.map((item) => optionNode(item))}
    </Fragment>
  );

  if (groupedOptions.length === 0) return null;

  return (
    <Box paddingY="2">
      {groupedOptions.map((item: any, index) =>
        !isOptGroup(item) ? optionNode(item, index === groupedOptions.length - 1) : optGroupNode(item),
      )}
    </Box>
  );
}

export default SingleListor;
